import React, { createContext, useContext, useState, ReactNode } from 'react';

function readInitialLocationFromURL(): string {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('location') ?? '';
  } catch {
    return '';
  }
}

// ?installationCode=MG-BJ-0042 : ouvre la liste filtrée sur une installation
// (lien depuis FieldMate).
function readInitialInstallationCodeFromURL(): string {
  if (typeof window === 'undefined') return '';
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('installationCode') ?? '';
  } catch {
    return '';
  }
}

export type TriStateFilter = 'ALL' | 'ON' | 'OFF' | 'NA'
export type ConsumptionSort = 'NONE' | 'ASC' | 'DESC'

interface SearchContextType {
  isTyping: boolean;
  setIsTyping: (isTyping: boolean) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  locationQuery: string;
  setLocationQuery: (v: string) => void;
  installationCodeQuery: string;
  setInstallationCodeQuery: (v: string) => void;
  relayFilter: TriStateFilter;
  setRelayFilter: (v: TriStateFilter) => void;
  onlineFilter: TriStateFilter;
  setOnlineFilter: (v: TriStateFilter) => void;
  consumptionSort: ConsumptionSort;
  setConsumptionSort: (v: ConsumptionSort) => void;
  consumptionMin: string;
  setConsumptionMin: (v: string) => void;
  consumptionMax: string;
  setConsumptionMax: (v: string) => void;
  phoneQuery: string;
  setPhoneQuery: (v: string) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  creditMin: string;
  setCreditMin: (v: string) => void;
  creditMax: string;
  setCreditMax: (v: string) => void;
  // Filtres "Poteau" (relié a un poteau) et "GPS" (localisation inscrite).
  // 'ALL' = tous, 'ON' = inscrit/relié, 'OFF' = non inscrit/non relié.
  poleFilter: TriStateFilter;
  setPoleFilter: (v: TriStateFilter) => void;
  gpsFilter: TriStateFilter;
  setGpsFilter: (v: TriStateFilter) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within SearchContextProvider');
  }
  return context;
};

export const SearchContextProvider = ({ children }: { children: ReactNode }) => {
  const [isTyping, setIsTyping] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [locationQuery, setLocationQuery] = useState<string>(readInitialLocationFromURL);
  const [installationCodeQuery, setInstallationCodeQuery] = useState<string>(readInitialInstallationCodeFromURL);
  const [relayFilter, setRelayFilter] = useState<TriStateFilter>('ALL');
  const [onlineFilter, setOnlineFilter] = useState<TriStateFilter>('ALL');
  const [consumptionSort, setConsumptionSort] = useState<ConsumptionSort>('NONE');
  const [consumptionMin, setConsumptionMin] = useState<string>('');
  const [consumptionMax, setConsumptionMax] = useState<string>('');
  const [phoneQuery, setPhoneQuery] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [creditMin, setCreditMin] = useState<string>('');
  const [creditMax, setCreditMax] = useState<string>('');
  const [poleFilter, setPoleFilter] = useState<TriStateFilter>('ALL');
  const [gpsFilter, setGpsFilter] = useState<TriStateFilter>('ALL');

  return (
    <SearchContext.Provider
      value={{
        isTyping,
        setIsTyping,
        selectedTags,
        setSelectedTags,
        locationQuery,
        setLocationQuery,
        installationCodeQuery,
        setInstallationCodeQuery,
        relayFilter,
        setRelayFilter,
        onlineFilter,
        setOnlineFilter,
        consumptionSort,
        setConsumptionSort,
        consumptionMin,
        setConsumptionMin,
        consumptionMax,
        setConsumptionMax,
        phoneQuery,
        setPhoneQuery,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        creditMin,
        setCreditMin,
        creditMax,
        setCreditMax,
        poleFilter,
        setPoleFilter,
        gpsFilter,
        setGpsFilter,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
